import { z } from '@customerio/hydra-validation';
import type { WarehouseSync as UIAPIWarehouseSync } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Warehouse-sync config (workspace-scoped singleton). At most one storage
 * destination is configured at a time; the integrations directory reads the
 * configured `type` and whether the sync is paused.
 */
export type WarehouseSync = UIAPIWarehouseSync;

const WarehouseSyncFromApi = readSchema<WarehouseSync>(
  z.looseObject({
    id: z.string(),
  }),
);

/** @public */
export const warehouseSync = defineEntity<WarehouseSync, WarehouseSync, void, undefined, 'workspace'>({
  name: 'warehouse_sync',
  scope: 'workspace',
  pk: () => 'current',
  schema: { read: WarehouseSyncFromApi },
});

export const warehouseSyncKeys = warehouseSync.keys;

/**
 * The storage destination a sync writes to. The wire model has no destination
 * field — it is derived from which `*_config` block is populated, and exactly
 * one ever is.
 */
export type WarehouseDestination = 's3' | 'gcs' | 'yandex' | 'azure_blob';

/** The request/response key carrying each destination's credentials. */
export const WAREHOUSE_DESTINATION_CONFIG_KEYS = Object.freeze({
  s3: 's3_config',
  gcs: 'gcs_config',
  yandex: 'yandex_config',
  azure_blob: 'azure_config',
} as const satisfies Record<WarehouseDestination, keyof UIAPIWarehouseSync>);

/**
 * The newest schema version the UI can upgrade a sync to. Hand-maintained:
 * there is no endpoint exposing the backend's `PublicMaxVersion`, so bumping
 * this pin is itself the schema-version release step (CDP-6248 tracks giving it
 * a canonical source).
 *
 * Keep this equal to `PublicMaxVersion` in `services/warehouse/export.go`. The
 * value is duplicated in `ui`'s `app/models/warehouse-sync.ts`, and the two
 * drifted once already: CDP-6567 raised Ember to 11 while this copy stayed at
 * the 9 it was ported with, so upgrades sent `version: 9` and skipped two
 * schema versions. Bump both, or neither.
 */
export const LATEST_SCHEMA_VERSION = 11;

/**
 * Destinations in resolution order. Only one `*_config` is ever populated, so
 * the order matters solely as a tiebreak on malformed data.
 */
const DESTINATION_PRECEDENCE: readonly WarehouseDestination[] = ['azure_blob', 'gcs', 's3', 'yandex'];

/** The configured storage destination, or `''` when none is. */
export function warehouseSyncType(sync: WarehouseSync | null): WarehouseDestination | '' {
  if (sync == null) return '';
  return DESTINATION_PRECEDENCE.find((d) => sync[WAREHOUSE_DESTINATION_CONFIG_KEYS[d]] != null) ?? '';
}

/** Whether the sync is behind the latest schema and can be upgraded. */
export function canUpdateWarehouseSchema(sync: WarehouseSync | null): boolean {
  return sync != null && sync.version < LATEST_SCHEMA_VERSION;
}

const EXPORT_KEYS: readonly (keyof UIAPIWarehouseSync)[] = [
  'deliveries',
  'contents',
  'metrics',
  'subjects',
  'outputs',
  'people',
  'attributes',
  'campaigns',
  'broadcasts',
  'objects',
  'object_attributes',
  'events',
];

/**
 * A sync is "paused" when every export toggle is off. Schema v5+ also gates on
 * the `inbound` toggle, but only when the workspace has the Twilio action
 * enabled.
 */
export function warehouseSyncIsPaused(sync: WarehouseSync | null, twilioEnabled: boolean): boolean {
  if (sync == null) return false;
  const keys = [...EXPORT_KEYS];
  if (sync.version >= 5 && twilioEnabled) {
    keys.push('inbound');
  }
  return keys.every((key) => sync[key] === false);
}

export function parseWarehouseSync(raw: unknown): WarehouseSync {
  return warehouseSync.parse(raw);
}

export type SchemaUpgradeExportState = {
  people: boolean;
  outputs: boolean;
  subjects: boolean;
};

export type SchemaUpgradeAlerts = SchemaUpgradeExportState;

/**
 * Which post-upgrade notices apply, keyed on the version the workspace is
 * coming *from*, and on whether that export was on beforehand.
 *
 * Call this with the record as it stands *before* the upgrade: the update
 * response carries the already-migrated record, so reading `version` and the
 * toggles back out of it afterwards yields all-`false` alerts.
 *
 * The backend switches on the current version when applying a `schema_version`
 * update: leaving v1/v2 starts sending people change events, so it disables
 * people imports and resets sync state; crossing v3 → v4 needs customer-side DB
 * work, so it disables subjects and outputs. There is no case for v4+, so an
 * upgrade from v4 or later is purely additive and raises none of these.
 */
export function getSchemaUpgradeAlerts(fromVersion: number, previous: SchemaUpgradeExportState): SchemaUpgradeAlerts {
  const resetsPeople = fromVersion < 3;
  const disablesSubjectsAndOutputs = fromVersion < 4;

  return {
    people: resetsPeople && previous.people,
    outputs: disablesSubjectsAndOutputs && previous.outputs,
    subjects: disablesSubjectsAndOutputs && previous.subjects,
  };
}
